import React, { useRef, useState, useEffect } from "react";
import { FaChartPie, FaGlobe, FaHandshake, FaUniversity, FaRegCommentDots, FaCog } from "react-icons/fa";
import { HiOutlineViewGrid, HiOutlinePresentationChartLine } from "react-icons/hi";
import { TbBrowserShare } from "react-icons/tb";
import { RiFileList3Line } from "react-icons/ri";
import { IoIosArrowDown, IoIosMenu } from "react-icons/io";

/* ===============================
   MENU
=============================== */
const menuItems = [
  { icon: <HiOutlineViewGrid />, label: "Dashboard" },
  { icon: <FaHandshake />, label: "Marketing" },
  { icon: <TbBrowserShare />, label: "Website Projects" },
  { icon: <FaGlobe />, label: "HR" },
  { icon: <FaChartPie />, label: "Analytics Reports" },

  {
    icon: <FaUniversity />,
    label: "Finance",
    subItems: [
      { name: "Finance Dashboard", value: "Finance" },
      { name: "Budgeting", value: "Budgeting" },
      { name: "Invoices", value: "Invoices" },
      { name: "Reports", value: "Finance Reports" }
    ],
  },

  {
    icon: <HiOutlinePresentationChartLine />,
    label: "Operations",
    subItems: [
      { name: "Operations Dashboard", value: "Operations" },
      { name: "Logistics", value: "Logistics" },
      { name: "Processes", value: "Processes" }
    ],
  },

  { icon: <RiFileList3Line />, label: "Documents" },
  { icon: <FaRegCommentDots />, label: "Message" },
  { icon: <FaCog />, label: "Settings" },
];

export default function Sidebar({ activePage, setActivePage }) {

  const itemRefs = useRef([]);
  const [sliderStyle, setSliderStyle] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  /* ===============================
     ACTIVE SLIDER (FIXED 🔥)
  =============================== */
  useEffect(() => {
    const activeIndex = menuItems.findIndex(
      (item) =>
        item.label === activePage ||
        (item.subItems &&
          item.subItems.some(sub => sub.value === activePage))
    );

    if (activeIndex === -1) return;

    const el = itemRefs.current[activeIndex];

    if (el) {
      setSliderStyle({
        transform: `translateY(${el.offsetTop}px)`,
        height: el.offsetHeight,
      });
    }
  }, [activePage, sidebarOpen]);

  /* ===============================
     AUTO OPEN DROPDOWN
  =============================== */
  useEffect(() => {
    const updatedDropdowns = {};

    menuItems.forEach((item) => {
      if (item.subItems) {
        const isActive = item.subItems.some(
          (sub) => sub.value === activePage
        );
        if (isActive) {
          updatedDropdowns[item.label] = true;
        }
      }
    });

    setOpenDropdowns((prev) => ({
      ...prev,
      ...updatedDropdowns
    }));
  }, [activePage]);

  /* ===============================
     RESPONSIVE FIX
  =============================== */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* =============================== */
  const toggleDropdown = (label) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const handleMobileClick = (label) => {
    setActivePage(label);
    setSidebarOpen(false);
  };

  return (
    <>
      {/* MOBILE MENU */}
      <button
        className="md:hidden fixed top-4 left-4 z-40 p-2 rounded-xl bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg hover:scale-105 transition"
        onClick={() => setSidebarOpen(true)}
      >
        <IoIosMenu className="text-2xl" />
      </button>

      {/* BACKDROP */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`
          fixed top-0 left-0 h-screen w-64 bg-white/80 backdrop-blur-xl border-r flex flex-col z-40
          transform transition-transform duration-300
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:p-6
        `}
      >

        {/* LOGO */}
        <div className="mb-10 px-4">
          <img
            src={`${process.env.PUBLIC_URL}/assets/adminlogo.png`}
            alt="Logo"
            className="w-52 drop-shadow-sm"
          />
        </div>

        {/* MENU */}
        <nav className="relative flex flex-col space-y-2 overflow-y-auto pr-2">

          {/* SLIDER */}
          <div
            className="absolute left-0 w-full rounded-xl bg-green-500/10 blur-[1px] transition-all duration-300"
            style={sliderStyle}
          />

          {menuItems.map(({ icon, label, subItems }, index) => {

            const isActive =
              label === activePage ||
              (subItems &&
                subItems.some(sub => sub.value === activePage));

            const isOpen = openDropdowns[label];

            return (
              <div key={label} className="flex flex-col">

                {/* MAIN ITEM */}
                <button
                  ref={(el) => (itemRefs.current[index] = el)}
                  onClick={() =>
                    subItems
                      ? toggleDropdown(label)
                      : handleMobileClick(label)
                  }
                  className={`
                    group relative flex items-center justify-between px-3 h-12 rounded-xl
                    transition-all duration-300 ease-out

                    ${
                      isActive
                        ? "bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg scale-[1.02]"
                        : "hover:bg-green-50 hover:scale-[1.02] hover:shadow-md"
                    }
                  `}
                >
                  <div className="flex items-center gap-3">

                    <span className={`
                      transition-all duration-300
                      ${isActive
                        ? "text-white scale-110"
                        : "text-gray-500 group-hover:text-green-600 group-hover:scale-110"}
                    `}>
                      {icon}
                    </span>

                    <span className="font-medium tracking-wide">
                      {label}
                    </span>
                  </div>

                  {subItems && (
                    <IoIosArrowDown
                      className={`transition duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  )}
                </button>

                {/* DROPDOWN */}
                {subItems && (
                  <div
                    className={`ml-10 flex flex-col transition-all duration-300 ${
                      isOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                    } overflow-hidden`}
                  >
                    {subItems.map((sub) => (
                      <button
                        key={sub.value}
                        onClick={() => handleMobileClick(sub.value)}
                        className={`
                          text-left py-2 px-3 text-sm rounded-lg transition-all duration-200
                          ${
                            activePage === sub.value
                              ? "bg-green-100 text-green-700 font-semibold"
                              : "hover:bg-green-50 hover:text-green-600"
                          }
                        `}
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                )}

              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}